import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UnifiedCampaign, CampaignWizardData, CampaignStatus, CampaignType, VoiceProvider, PhoneProvider } from '@/types/unifiedCampaign';
import { useAuth } from './useAuth';
import { useOptimizedSupabaseScripts } from './useOptimizedSupabaseScripts';
import { useToast } from '@/hooks/use-toast';
import { requestThrottler } from '@/utils/requestThrottle';

// Global cache for campaigns
let globalCampaignsCache: UnifiedCampaign[] = [];
let lastFetchTime = 0;
let currentUserId: string | null = null;
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

export function useOptimizedUnifiedCampaigns() {
  const [campaigns, setCampaigns] = useState<UnifiedCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { scripts: cachedScripts } = useOptimizedSupabaseScripts();
  const { toast } = useToast();
  const isMountedRef = useRef(true);

  // Fetch campaigns from Supabase with caching
  const fetchCampaigns = async (forceRefresh = false) => {
    if (!user) {
      setCampaigns([]);
      setLoading(false);
      return;
    }
    
    const now = Date.now();
    const userId = user.id;
    
    // Use cache if valid and for same user
    if (!forceRefresh && 
        globalCampaignsCache.length > 0 && 
        currentUserId === userId && 
        (now - lastFetchTime) < CACHE_DURATION) {
      setCampaigns(globalCampaignsCache);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Use request throttler to prevent duplicate calls
      const cacheKey = `campaigns-${userId}`;
      
      const data = await requestThrottler.throttle(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('campaigns')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (error) throw error;
          return data || [];
        },
        { ttl: CACHE_DURATION }
      );

      const transformedCampaigns = data.map(campaign => ({
        ...campaign,
        campaign_type: campaign.campaign_type as CampaignType,
        status: campaign.status as CampaignStatus,
        phone_provider: campaign.phone_provider as PhoneProvider,
        voice_provider: campaign.voice_provider as VoiceProvider,
        ai_model: campaign.ai_model as 'grok' | 'openai',
        created_at: new Date(campaign.created_at),
        updated_at: new Date(campaign.updated_at),
        started_at: campaign.started_at ? new Date(campaign.started_at) : undefined,
        completed_at: campaign.completed_at ? new Date(campaign.completed_at) : undefined,
        activated_at: campaign.activated_at ? new Date(campaign.activated_at) : undefined,
        deactivated_at: campaign.deactivated_at ? new Date(campaign.deactivated_at) : undefined,
        scheduled_start: campaign.scheduled_start ? new Date(campaign.scheduled_start) : undefined,
        tags: campaign.tags || [],
        stats: typeof campaign.stats === 'string' ? JSON.parse(campaign.stats) : campaign.stats,
        transfer_settings: typeof campaign.transfer_settings === 'string' 
          ? JSON.parse(campaign.transfer_settings) 
          : campaign.transfer_settings,
        voicemail_settings: typeof campaign.voicemail_settings === 'string'
          ? JSON.parse(campaign.voicemail_settings)
          : campaign.voicemail_settings,
        ai_settings: typeof campaign.ai_settings === 'string'
          ? JSON.parse(campaign.ai_settings)
          : campaign.ai_settings,
      }));

      // Update global cache
      globalCampaignsCache = transformedCampaigns;
      lastFetchTime = now;
      currentUserId = userId;

      if (isMountedRef.current) {
        setCampaigns(transformedCampaigns);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch campaigns');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [user]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const createCampaign = async (wizardData: CampaignWizardData): Promise<UnifiedCampaign> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Check if we need to add the warm intro mode tag using cached script data
      let tags = wizardData.tags || [];
      
      // Get the script ID to check for safe mode (cached from script hook)
      const scriptId = wizardData.outbound_script_id || wizardData.script_id;
      if (scriptId && cachedScripts) {
        const script = cachedScripts.find(s => s.id === scriptId);
        
        // If script uses low risk mode, add the warm intro tag
        if (script?.use_low_risk_mode && !tags.includes('🕊️ Warm Intro Mode')) {
          tags = [...tags, '🕊️ Warm Intro Mode'];
        }
      }

      // Compute the primary script_id based on campaign type
      let primaryScriptId = wizardData.script_id;
      if (wizardData.campaign_type === 'outbound_inbound') {
        primaryScriptId = wizardData.outbound_script_id;
      }

      // Destructure vapi_key_name to prevent it from being passed as top-level column
      const { vapi_key_name, ...cleanWizardData } = wizardData;
      
      const campaignData = {
        user_id: user.id,
        ...cleanWizardData,
        script_id: primaryScriptId, // Set the computed primary script_id
        tags,
        status: 'draft' as CampaignStatus,
        ai_settings: wizardData.ai_settings || {},
        transfer_settings: wizardData.transfer_settings || {
          enabled: false,
          transferNumber: null,
          transferConditions: []
        },
        voicemail_settings: wizardData.voicemail_settings || {
          enabled: false,
          customMessage: null
        },
        max_call_duration: wizardData.max_call_duration || 30,
        // Let database trigger handle record_calls default for inbound campaigns  
        ...(wizardData.record_calls !== undefined && { record_calls: wizardData.record_calls }),
        scheduled_start: wizardData.scheduled_start?.toISOString(),
      };

      const { data, error } = await supabase
        .from('campaigns')
        .insert([campaignData])
        .select()
        .single();

      if (error) throw error;

      // Auto-create sister inbound campaign for outbound_inbound type
      if (wizardData.campaign_type === 'outbound_inbound') {
        const inboundData = {
          ...campaignData,
          name: `${wizardData.name} (Inbound)`,
          campaign_type: 'inbound',
          script_id: wizardData.inbound_script_id, // Use inbound script for sister campaign
          prospect_list_id: null, // Inbound doesn't need prospect list
        };

        await supabase
          .from('campaigns')
          .insert([inboundData]);
      }

      // Clear cache and refresh
      requestThrottler.clearPattern('campaigns-');
      await fetchCampaigns(true);
      
      // Transform the data response to match our interface
      const transformedData: UnifiedCampaign = {
        ...data,
        campaign_type: data.campaign_type as CampaignType,
        status: data.status as CampaignStatus,
        phone_provider: data.phone_provider as PhoneProvider,
        voice_provider: data.voice_provider as VoiceProvider,
        ai_model: data.ai_model as 'grok' | 'openai',
        created_at: new Date(data.created_at),
        updated_at: new Date(data.updated_at),
        started_at: data.started_at ? new Date(data.started_at) : undefined,
        completed_at: data.completed_at ? new Date(data.completed_at) : undefined,
        activated_at: data.activated_at ? new Date(data.activated_at) : undefined,
        deactivated_at: data.deactivated_at ? new Date(data.deactivated_at) : undefined,
        scheduled_start: data.scheduled_start ? new Date(data.scheduled_start) : undefined,
        tags: data.tags || [],
        stats: typeof data.stats === 'string' ? JSON.parse(data.stats) : data.stats,
        transfer_settings: typeof data.transfer_settings === 'string' 
          ? JSON.parse(data.transfer_settings) 
          : data.transfer_settings,
        voicemail_settings: typeof data.voicemail_settings === 'string'
          ? JSON.parse(data.voicemail_settings)
          : data.voicemail_settings,
        ai_settings: typeof data.ai_settings === 'string'
          ? JSON.parse(data.ai_settings)
          : data.ai_settings,
      };
      
      return transformedData;
    } catch (err) {
      console.error('Error creating campaign:', err);
      throw err;
    }
  };

  const updateCampaign = async (id: string, updates: Partial<UnifiedCampaign>) => {
    try {
      // Transform frontend types to database types
      const dbUpdates: any = {};
      
      Object.keys(updates).forEach(key => {
        const value = updates[key as keyof UnifiedCampaign];
        if (value instanceof Date) {
          dbUpdates[key] = value.toISOString();
        } else if (typeof value === 'object' && value !== null) {
          dbUpdates[key] = JSON.stringify(value);
        } else {
          dbUpdates[key] = value;
        }
      });
      
      dbUpdates.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('campaigns')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      // Clear cache and refresh
      requestThrottler.clearPattern('campaigns-');
      await fetchCampaigns(true);
    } catch (err) {
      console.error('Error updating campaign:', err);
      throw err;
    }
  };

  const updateCampaignStatus = async (id: string, status: CampaignStatus) => {
    const targetCampaign = globalCampaignsCache.find(c => c.id === id) || campaigns.find(c => c.id === id);
    if (!targetCampaign) {
      console.error('Campaign not found:', id);
      return;
    }

    // Validate inbound campaign activation
    if (status === 'active' && targetCampaign.campaign_type === 'inbound') {
      const { validateInboundCampaignActivation } = await import('@/utils/campaignValidation');
      const validation = validateInboundCampaignActivation(targetCampaign, campaigns);
      
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
    }

    const updates: Partial<UnifiedCampaign> = { status };
    const now = new Date();

    if (status === 'active') {
      updates.started_at = now;
      if (campaigns.find(c => c.id === id)?.campaign_type === 'inbound') {
        updates.activated_at = now;
      }
    } else if (status === 'completed' || status === 'cancelled') {
      updates.completed_at = now;
    } else if (status === 'inactive') {
      updates.deactivated_at = now;
    }
    
    // Clear vapi_assistant_id when pausing to prevent cross-contamination
    if (status === 'paused') {
      updates.vapi_assistant_id = null;
    }

    await updateCampaign(id, updates);

    // Handle campaign execution based on type
    // Use globalCampaignsCache (updated by fetchCampaigns above) instead of stale React state
    const campaign = globalCampaignsCache.find(c => c.id === id) || campaigns.find(c => c.id === id);

    if (campaign && status === 'active') {
      // For outbound_inbound: provision inbound first, then attempt outbound
      if (campaign.campaign_type === 'outbound_inbound') {
        try {
          await configureInboundWebhook(campaign);
        } catch (error) {
          console.error('Inbound webhook configuration failed:', error);
          // Don't prevent campaign from starting
        }
        
        // Attempt outbound execution but don't fail if it errors
        try {
          await startCampaignExecution(campaign);
        } catch (error) {
          console.error('Outbound execution failed:', error);
          toast({
            title: "Partial Campaign Start",
            description: "Campaign started but outbound execution failed",
            variant: "destructive"
          });
        }
      }
      // For inbound only: just configure webhook (auto-rebuild)
      else if (campaign.campaign_type === 'inbound') {
        try {
          await configureInboundWebhook(campaign);
        } catch (error) {
          console.error('Inbound webhook configuration failed:', error);
          // Don't prevent campaign from starting
        }
      }
      // For outbound only: just start execution
      else if (campaign.campaign_type === 'outbound') {
        await startCampaignExecution(campaign);
      }
    }
    
    // Handle pause/stop for outbound components
    if (campaign && (campaign.campaign_type === 'outbound' || campaign.campaign_type === 'outbound_inbound')) {
      if (status === 'paused') {
        await pauseCampaignExecution(id);
      } else if (status === 'completed' || status === 'cancelled') {
        await stopCampaignExecution(id);
      }
    }
  };

  const startCampaignExecution = async (campaign: UnifiedCampaign) => {
    try {
      // Get script and prospect list data from Supabase
      const scriptId = campaign.outbound_script_id || campaign.script_id;
      const prospectListId = campaign.prospect_list_id;

      if (!scriptId || !prospectListId) {
        throw new Error('Campaign missing script ID or prospect list ID');
      }

      // Fetch script from Supabase
      const { data: script, error: scriptError } = await supabase
        .from('scripts')
        .select('*')
        .eq('id', scriptId)
        .eq('user_id', user?.id)
        .single();

      if (scriptError || !script) {
        throw new Error(`Script not found: ${scriptError?.message || 'Unknown error'}`);
      }

      // Fetch prospect list from Supabase
      const { data: prospectList, error: prospectError } = await supabase
        .from('prospect_lists')
        .select('*')
        .eq('id', prospectListId)
        .eq('user_id', user?.id)
        .single();

      if (prospectError || !prospectList) {
        throw new Error(`Prospect list not found: ${prospectError?.message || 'Unknown error'}`);
      }

      // Ensure campaign has VAPI assistant before execution
      if (!campaign.vapi_assistant_id) {
        
        const { data: provisionData, error: provisionError } = await supabase.functions.invoke('initiate-vapi-call', {
          body: { 
            campaignId: campaign.id,
            scriptId: script.id,
            provisionOnly: true  // Flag to only provision assistant, not make call
          }
        });

        if (provisionError || !provisionData?.assistantId) {
          throw new Error(`Failed to provision VAPI assistant: ${provisionError?.message || 'Unknown error'}`);
        }

        // Update local campaign object with new assistant ID
        campaign.vapi_assistant_id = provisionData.assistantId;
      }
      
      // Get next prospect from the list
      const prospects = Array.isArray(prospectList.prospects) ? prospectList.prospects : [];
      if (prospects.length === 0) {
        throw new Error('No prospects available in the list');
      }

      // Get the first prospect (in a real implementation, you'd track which prospects have been called)
      const nextProspect = prospects[0] as any;
      
      if (!nextProspect?.phone) {
        throw new Error('No phone number available for prospect');
      }

      // Initiate VAPI call with proper mergeContext
      const { data, error } = await supabase.functions.invoke('initiate-vapi-call', {
        body: { 
          campaignId: campaign.id,
          prospectId: nextProspect.id || nextProspect.name || 'unknown',
          scriptId: script.id,
          phoneNumber: nextProspect.phone,
          mergeContext: {
            industry: nextProspect.industry || ''
          }
        }
      });

      if (error) {
        console.error('Error initiating VAPI call:', error);
        toast({
          title: "Campaign Start Failed",
          description: "Failed to initiate VAPI call: " + error.message,
          variant: "destructive"
        });
        throw error;
      }

      // Check if the edge function returned an error in the response
      if (data && !data.ok) {
        const errorMsg = data.error || data.message || "VAPI call failed";
        const traceInfo = data.traceId ? ` (Trace: ${data.traceId})` : '';
        console.error('VAPI call failed:', data.error || data.message, { traceId: data.traceId });
        toast({
          title: "Campaign Start Failed", 
          description: errorMsg + traceInfo,
          variant: "destructive"
        });
        throw new Error(errorMsg);
      }

      toast({
        title: "Campaign Started",
        description: `Call initiated successfully for ${nextProspect.name}`,
        variant: "default"
      });
    } catch (error) {
      console.error('Failed to start campaign execution:', error);
      throw error;
    }
  };

  const pauseCampaignExecution = async (campaignId: string) => {
    try {
      const { error } = await supabase.functions.invoke('execute-campaign', {
        body: {
          campaignId,
          action: 'pause'
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to pause campaign execution:', error);
      toast({
        title: 'Failed to Pause Campaign',
        description: error instanceof Error ? error.message : 'An error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const stopCampaignExecution = async (campaignId: string) => {
    try {
      const { error } = await supabase.functions.invoke('execute-campaign', {
        body: {
          campaignId,
          action: 'stop'
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to stop campaign execution:', error);
      toast({
        title: 'Failed to Stop Campaign',
        description: error instanceof Error ? error.message : 'An error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const configureInboundWebhook = async (campaign: UnifiedCampaign) => {
    // Configure inbound call webhook for this campaign
    
    try {
      const { data, error } = await supabase.functions.invoke('configure-inbound-webhook', {
        body: {
          campaignId: campaign.id,
          phoneNumber: campaign.phone_number,
          scriptId: campaign.script_id,
          aiModel: campaign.ai_model,
          voiceId: campaign.voice_id
        }
      });

      if (error) {
        console.error('Error configuring inbound webhook:', error);
        
        // Check if it's a network/function error vs validation error
        if (error.message.includes('FunctionsFetchError') || error.message.includes('network')) {
          toast({
            title: "Function Unavailable",
            description: "Inbound assistant configuration service is not available. Please ensure all Supabase functions are deployed.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Provisioning Failed",
            description: `Failed to configure inbound assistant: ${error.message}`,
            variant: "destructive",
          });
        }
        throw error;
      }

      if (data?.error) {
        console.error('Inbound webhook configuration error:', data.error);
        toast({
          title: "Provisioning Failed",
          description: `Failed to configure inbound assistant: ${data.error}`,
          variant: "destructive",
        });
        throw new Error(data.error);
      }

      toast({
        title: "Assistant Configured",
        description: "Inbound assistant has been successfully configured",
      });
    } catch (error) {
      // Re-throw to maintain error flow but with better error context
      console.error('configureInboundWebhook failed:', error);
      throw error;
    }
  };

  const rebuildInboundAssistant = async (campaignId: string) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign || campaign.campaign_type !== 'inbound') {
      toast({
        title: "Invalid Campaign",
        description: "Only inbound campaigns can have their assistants rebuilt",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Rebuilding Assistant",
        description: "Rebuilding inbound assistant...",
      });
      
      await configureInboundWebhook(campaign);
      
      // Update the vapi_assistant_id if returned
      await fetchCampaigns(true);
    } catch (error) {
      console.error('Failed to rebuild assistant:', error);
      // Error toast already shown in configureInboundWebhook
    }
  };

  const deleteCampaign = async (id: string) => {
    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Clear cache and refresh
      requestThrottler.clearPattern('campaigns-');
      await fetchCampaigns(true);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      throw err;
    }
  };

  const getCampaignStats = () => {
    const total = campaigns.length;
    const active = campaigns.filter(c => c.status === 'active').length;
    const draft = campaigns.filter(c => c.status === 'draft').length;
    const completed = campaigns.filter(c => c.status === 'completed').length;

    return { total, active, draft, completed };
  };

  return {
    campaigns,
    loading,
    error,
    createCampaign,
    updateCampaign,
    updateCampaignStatus,
    deleteCampaign,
    rebuildInboundAssistant,
    getCampaignStats,
    refreshCampaigns: () => fetchCampaigns(true),
  };
}

// Utility to clear global cache (useful for logout)
export const clearCampaignsCache = () => {
  globalCampaignsCache = [];
  lastFetchTime = 0;
  currentUserId = null;
  requestThrottler.clearPattern('campaigns-');
};